import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_PROJECTINVESTIGATOR_LOAD_BY_ID,
    ACTION_PROJECTINVESTIGATORS_LOAD_BY_PROJECTID,
    ACTION_PROJECTINVESTIGATOR_LOAD_FAILURE,
    ACTION_PROJECTINVESTIGATOR_LOAD_SUCCESS,

    ACTION_PROJECTINVESTIGATOR_CREATE,
    ACTION_PROJECTINVESTIGATOR_CREATE_SUCCESS,
    ACTION_PROJECTINVESTIGATOR_CREATE_FAILURE,

    ACTION_PROJECTINVESTIGATOR_SAVE,
    ACTION_PROJECTINVESTIGATOR_SAVE_SUCCESS,
    ACTION_PROJECTINVESTIGATOR_SAVE_FAILURE,

    ACTION_PROJECTINVESTIGATOR_VALIDATE,
    ACTION_PROJECTINVESTIGATOR_VALIDATE_SUCCESS,
    ACTION_PROJECTINVESTIGATOR_VALIDATE_FAILURE,

    ACTION_PROJECTINVESTIGATOR_CLEAR,
    ProjectInvestigatorAction
} from '../actions/projectInvestigator';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IProjectInvestigatorState {
    formData: Dtos.ProjectInvestigator | Dtos.ProjectInvestigator[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
}

const initialState: IProjectInvestigatorState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
};


function loading(state: IProjectInvestigatorState, _isLoading: boolean): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IProjectInvestigatorState,
    formData: Dtos.ProjectInvestigator | Dtos.ProjectInvestigator[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function loadFailure(state: IProjectInvestigatorState, responseStatus: Dtos.ResponseStatus): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IProjectInvestigatorState, propertiesToClear?: string[]): IProjectInvestigatorState {

    if (!propertiesToClear) {
        const newState: IProjectInvestigatorState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function creating(state: IProjectInvestigatorState, _isCreating: boolean): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IProjectInvestigatorState,
    formData: Dtos.ProjectInvestigator,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IProjectInvestigatorState, responseStatus: Dtos.ResponseStatus): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function saving(state: IProjectInvestigatorState, _isSaving: boolean): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IProjectInvestigatorState,
    formData: Dtos.ProjectInvestigator | Dtos.ProjectInvestigator[],
    formState: Dtos.FormState,
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        lookups: {
            $set: lookups
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IProjectInvestigatorState, responseStatus: Dtos.ResponseStatus): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: IProjectInvestigatorState, _isValidating: boolean): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IProjectInvestigatorState,
    formData: Dtos.ProjectInvestigator,
    formState: Dtos.FormState,
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        lookups: {
            $set: lookups
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IProjectInvestigatorState, responseStatus: Dtos.ResponseStatus): IProjectInvestigatorState {
    const newState: IProjectInvestigatorState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const ProjectInvestigatorReducer: Reducer<IProjectInvestigatorState> = (state: IProjectInvestigatorState = initialState, action: ProjectInvestigatorAction) => {
    switch (action.type) {
        case ACTION_PROJECTINVESTIGATOR_LOAD_BY_ID:
        case ACTION_PROJECTINVESTIGATORS_LOAD_BY_PROJECTID:
            return loading(state, true);
        case ACTION_PROJECTINVESTIGATOR_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_PROJECTINVESTIGATOR_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_PROJECTINVESTIGATOR_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_PROJECTINVESTIGATOR_CREATE:
            return creating(state, true);
        case ACTION_PROJECTINVESTIGATOR_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_PROJECTINVESTIGATOR_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_PROJECTINVESTIGATOR_SAVE:
            return saving(state, true);
        case ACTION_PROJECTINVESTIGATOR_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.lookups, action.responseStatus);
        case ACTION_PROJECTINVESTIGATOR_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_PROJECTINVESTIGATOR_VALIDATE:
            return validating(state, true);
        case ACTION_PROJECTINVESTIGATOR_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.lookups, action.responseStatus);
        case ACTION_PROJECTINVESTIGATOR_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
    }

    return state;
}

export default ProjectInvestigatorReducer;